import { useState } from 'react';
import { graphql } from '@/gql';
import { apolloClient } from '@/shared/lib/api/apollo';
import { toast } from 'sonner';

/**
 * The account's own operations, over GraphQL like everything else in the
 * admin. The hooks keep the `{ mutateAsync, isPending }` surface their
 * consumers already use, without TanStack behind them.
 */

/** The signed-in account as the API sees it now; the refresh button reads this. */
export const VIEWER_PROFILE = graphql(`
  query ViewerProfile {
    viewer {
      id
      email
      isEmailVerified
      firstName
      lastName
      role
      accountType
      internalStatus
      permissions
    }
  }
`);

/**
 * The admin's sign-in, called immediately after the Google popup resolves.
 *
 * It is what creates the `users` row for a first-time staff member and what
 * refuses an address off the allowed domains, so nothing else may run before
 * it — the login form awaits this and signs the user back out if it throws.
 */
export const INTERNAL_SIGN_IN = graphql(`
  mutation InternalSignIn {
    internalSignIn {
      uid
      email
      isEmailVerified
      firstName
      lastName
      role
      accountType
      internalStatus
      permissions
    }
  }
`);

export const useRefreshProfile = () => {
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = async () => {
    setIsPending(true);
    try {
      await apolloClient.query({ query: VIEWER_PROFILE, fetchPolicy: 'network-only' });
      // The refresh may have changed role/permissions — refetch what's on screen.
      await apolloClient.refetchQueries({ include: 'active' });
      toast.success('Profile refreshed successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to refresh profile');
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending };
};
